import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { SiteProfile, MultiSiteSettings, TreatmentArea, SiteTarget } from '../types';
import { DEFAULT_MULTI_SITE_SETTINGS, detectCurrentSiteMode, isProductionDomain } from '../utils/siteConfig';
import { TREATMENT_AREAS } from '../data/treatmentAreas';
import { FEMALE_TREATMENT_AREAS } from '../data/femaleTreatmentAreas';

interface SiteContextType {
  siteKey: SiteTarget;
  setSiteKey: (key: SiteTarget) => void;
  siteProfile: SiteProfile;
  multiSiteSettings: MultiSiteSettings;
  isApollon: boolean;
  isFemale: boolean;
  isErkek: boolean;
  treatmentAreas: TreatmentArea[];
  updateSiteProfile: (key: SiteTarget, updated: Partial<SiteProfile>) => void;
  refreshProfiles: () => Promise<void>;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export const SiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [multiSiteSettings, setMultiSiteSettings] = useState<MultiSiteSettings>(DEFAULT_MULTI_SITE_SETTINGS);

  // Detect initial site using strict domain rule on production, or query param in preview
  const [siteKey, setSiteKeyState] = useState<SiteTarget>(() => {
    return detectCurrentSiteMode();
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname.toLowerCase().trim();
      if (isProductionDomain(hostname)) {
        // Remove any ?site= or ?mode= query parameters on production to prevent Googlebot parameter pollution
        const url = new URL(window.location.href);
        if (url.searchParams.has('site') || url.searchParams.has('mode')) {
          url.searchParams.delete('site');
          url.searchParams.delete('mode');
          window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
        }
      }
    }
  }, []);

  const setSiteKey = (key: SiteTarget) => {
    setSiteKeyState(key);
    if (typeof window !== 'undefined') {
      localStorage.setItem('bursa_active_site_preview', key);
      localStorage.setItem('active_preview_site_mode', key);
      const hostname = window.location.hostname.toLowerCase().trim();
      if (!isProductionDomain(hostname)) {
        const url = new URL(window.location.href);
        url.searchParams.set('site', key);
        window.history.replaceState({}, '', url.toString());
      }
    }
  };

  const refreshProfiles = async () => {
    try {
      const res = await fetch('/api/site-profiles');
      const data = await res.json();
      if (data.success && data.profiles) {
        setMultiSiteSettings((prev) => ({
          ...DEFAULT_MULTI_SITE_SETTINGS,
          ...data.profiles,
        }));
      }
    } catch (e) {
      console.warn('[SiteContext] Failed to fetch site profiles, using local defaults', e);
    }
  };

  useEffect(() => {
    refreshProfiles();
  }, []);

  const siteProfile = useMemo(() => {
    const raw = multiSiteSettings[siteKey] || DEFAULT_MULTI_SITE_SETTINGS[siteKey] || DEFAULT_MULTI_SITE_SETTINGS.apollon;
    const defaultForKey = DEFAULT_MULTI_SITE_SETTINGS[siteKey] || DEFAULT_MULTI_SITE_SETTINGS.apollon;
    
    return {
      ...defaultForKey,
      ...raw,
      siteName: raw?.siteName || defaultForKey.siteName || (siteKey === 'apollon' ? 'Apollon Güzellik Merkezi' : siteKey === 'kadin' ? 'Bursa Kadın Lazer Epilasyon' : 'Bursa Erkek Lazer Epilasyon'),
      clinicName: raw?.clinicName || defaultForKey.clinicName || (siteKey === 'apollon' ? 'Apollon Güzellik & Medikal Estetik Merkezi' : siteKey === 'kadin' ? 'Bursa Kadın Lazer Epilasyon Kliniği' : 'Bursa Erkek Lazer Epilasyon Kliniği'),
      metaTitle: raw?.metaTitle || defaultForKey.metaTitle,
      metaDescription: raw?.metaDescription || defaultForKey.metaDescription,
    };
  }, [multiSiteSettings, siteKey]);

  const isApollon = siteKey === 'apollon';
  const isFemale = siteKey === 'kadin';
  const isErkek = siteKey === 'erkek';

  const treatmentAreas = useMemo(() => {
    return isFemale ? FEMALE_TREATMENT_AREAS : TREATMENT_AREAS;
  }, [isFemale]);

  const updateSiteProfile = (key: SiteTarget, updated: Partial<SiteProfile>) => {
    setMultiSiteSettings((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...updated,
      },
    }));
  };

  return (
    <SiteContext.Provider
      value={{
        siteKey,
        setSiteKey,
        siteProfile,
        multiSiteSettings,
        isApollon,
        isFemale,
        isErkek,
        treatmentAreas,
        updateSiteProfile,
        refreshProfiles,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};
